import { useState } from 'react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { FileText, Eye, Trash2, MoreVertical, BarChart3, RefreshCw, Loader2, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Upload } from '@/types'
import { useUploadStore } from '@/stores/uploadStore'
import { useAuth } from '@/contexts/AuthContext'
import { DataTable, DataTableColumn } from '@/components/common/DataTable'
import { DataCard, EmptyState } from '@/components/common'

interface UploadsListProps {
  uploads: Upload[]
  onViewData: (upload: Upload) => void
  onViewAIResults?: (upload: Upload) => void
  onStartStagingWorkflow?: (stagingId: string) => void
}

export function UploadsList({ uploads, onViewData, onViewAIResults, onStartStagingWorkflow }: UploadsListProps) {
  const [deleteUploadId, setDeleteUploadId] = useState<string | null>(null)
  const { currentUser } = useAuth()
  const {
    deleteUpload,
    retryProcessing,
    retryStagingProcessing,
    isProcessingAI
  } = useUploadStore()

  const handleDeleteUpload = async (uploadId: string) => {
    if (!currentUser) return
    await deleteUpload(uploadId, currentUser)
    setDeleteUploadId(null)
  }

  const handleRetryProcessing = async (uploadId: string) => {
    if (!currentUser) return

    // Use new staging-based retry for uploads created with new architecture
    await retryStagingProcessing(uploadId, currentUser)
  }

  const getStatusBadge = (upload: Upload) => {
    switch (upload.status) {
      case 'uploading':
        return <Badge variant="outline">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Laddar upp
        </Badge>
      case 'processing':
        return <Badge variant="outline">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Bearbetar
        </Badge>
      case 'processed':
        return <Badge variant="outline">Bearbetad</Badge>
      case 'completed':
        return <Badge variant="default">
          Slutförd
        </Badge>
      case 'failed':
        return <Badge variant="destructive">
          {upload.aiError ? 'Bearbetningsfel' : 'Misslyckades'}
        </Badge>
      default:
        return <Badge variant="secondary">{upload.status}</Badge>
    }
  }

  const getPlatformName = (platform: string) => {
    if (!platform) return 'Ej vald'

    switch (platform) {
      case 'bolt':
        return 'Bolt'
      case 'uber':
        return 'Uber'
      case 'driver_import':
        return 'Förarimport'
      default:
        return platform.charAt(0).toUpperCase() + platform.slice(1)
    }
  }

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 B'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Define table columns
  const columns: DataTableColumn<Upload>[] = [
    {
      key: 'fileName',
      header: 'Filnamn',
      cell: (value: string) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      key: 'uploadedAt',
      header: 'Datum',
      cell: (value: Date) => format(value, 'PPp', { locale: sv }),
      className: 'hidden sm:table-cell'
    },
    {
      key: 'rowCount',
      header: 'Rader',
      cell: (value: number) => value.toLocaleString('sv-SE'),
      align: 'right',
      className: 'hidden md:table-cell'
    },
    {
      key: 'fileSize',
      header: 'Storlek',
      cell: (value: number) => formatFileSize(value),
      align: 'right',
      className: 'hidden md:table-cell'
    },
    {
      key: 'platform',
      header: 'Plattform',
      cell: (value: string) => getPlatformName(value)
    },
    {
      key: 'status',
      header: 'Status',
      cell: (value: string, row: Upload) => getStatusBadge(row)
    },
    {
      key: 'id' as keyof Upload,
      header: '',
      width: '50px',
      cell: (value: string, row: Upload) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Öppna meny</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewData(row)}>
              <Eye className="mr-2 h-4 w-4" />
              Visa data
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Processing Options */}
            {row.status === 'failed' && row.aiError && (
              <DropdownMenuItem
                onClick={() => handleRetryProcessing(row.id)}
                disabled={isProcessingAI}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Försök igen
              </DropdownMenuItem>
            )}

            {row.status === 'processed' && row.aiResults && onViewAIResults && (
              <DropdownMenuItem onClick={() => onViewAIResults(row)}>
                <BarChart3 className="mr-2 h-4 w-4" />
                Visa resultat
              </DropdownMenuItem>
            )}

            {row.stagingId && onStartStagingWorkflow && (
              <DropdownMenuItem onClick={() => {
                console.log('[UploadsList] Starting staging workflow for upload:', row.id, 'staging:', row.stagingId)
                onStartStagingWorkflow(row.stagingId!)
              }}>
                <DollarSign className="mr-2 h-4 w-4" />
                Skapa inkomster
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => setDeleteUploadId(row.id)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Ta bort
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ]

  if (uploads.length === 0) {
    return (
      <DataCard>
        <EmptyState
          icon={<FileText className="h-12 w-12 text-muted-foreground" />}
          title="Inga uppladdningar än"
          message="Ladda upp din första CSV eller Excel-fil för att komma igång"
        />
      </DataCard>
    )
  }

  return (
    <>
      <DataCard title={`Uppladdade filer (${uploads.length})`}>
        <DataTable
          data={uploads}
          columns={columns}
          emptyMessage="Inga filer hittades"
        />
      </DataCard>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteUploadId !== null} onOpenChange={() => setDeleteUploadId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort uppladdning</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort denna uppladdning? Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUploadId && handleDeleteUpload(deleteUploadId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
